import React, { useEffect, useRef, useState } from 'react';
import Search from '../searchComponents/Search';
import EntityServiceAPI from '../../../api/EntityServiceAPI';
import DataSearchAPI from 'api/DataSearch';
import { Redirect } from 'react-router-dom';
import { runAIEvaluationSingleEntity } from '../searchComponents/AIEvaluate';
import { includeFields } from 'utils/OSDataHelper';
import { Modal } from '@mui/material';
import EntityFilterOptions from '../filterOpts/EntFilterOpts';
import DateSelector from '../filters/DateSelector';
import { format } from 'date-fns';
import CasesAPI from 'api/CasesAPI';
import { useSnackbar } from 'notistack';
import pako from 'pako';
import { Buffer } from 'buffer';
import { handleShareTicketNotification } from 'utils/NotificationsFuncs';
import { useLogAppAction } from 'hooks/useLogAppAction';
import {
  ENTITY_AI_EVAL_NO_STRONG_MATCHES_MESSAGE,
  buildInteractiveEntityAiSampleScoreParams,
  buildInteractiveEntityHitsScoreParams,
  buildInteractiveEntityMatchTotalScoreParams,
  planInteractiveEntityAiEvaluation,
  planInteractiveToolHitsFetch,
  resolveEntityScreenRerunReason,
  selectInteractiveEntityAiSources,
  topHitSource,
} from '../tables/entityTable/entityHitsPagination';
import type {
  EntityScreenInputMode,
  EntityScreenQuery,
  EntityScreenRerunReason,
  EntityScreenRunOptions,
  EntityScreenSubject,
} from '../tables/entityTable/entityHitsPagination';
import type {
  ListCatalogRow,
  SavedFilter,
  SavedFilterKind,
  Scope,
} from 'components/ScreeningComposer/types';
import {
  countScope,
  emptyScope,
  flattenListAggs,
  listScopeParams,
  savedFields,
} from 'components/ScreeningComposer/utils';
import { buildEntityTableFilterParams } from '../tables/entityTable/entityTableFilterParams';
import type { EntityTableExportContext } from '../tables/entityTable/entityTableFilterParams';

// Record fields sent to the AI Evaluation.
const ENTITY_AI_FIELDS = [
  'Name of Entity',
  'Alternative Names',
  'Related Entity',
  'Entity Address',
  'Profile',
  'Entity Country',
  'Agency',
  'List Name',
  // Sent so the AI risk model can key on a stable doc id (dedup /
  // top-10 aggregate), sort by recency, and apply BITE Lens priority.
  'BITE Record ID',
  'Date Updated on BITE Platform',
  'BITE Lens',
];

interface IEntitiesProps {
  filters: any;
  setFilters: any;
  setShowFullRecord: any;
  showFilters: boolean;
  setShowFilters: any;
  cur_user: any;
  userHasEntitySearch: boolean;
  inputs: any;
  setInputs: any;
  page: number;
  setPage: any;
  entityState: any;
  setEntityState: any;
}

const Entities: React.FC<IEntitiesProps> = ({
  entityState,
  setEntityState,
  page,
  setPage,
  inputs,
  setInputs,
  userHasEntitySearch,
  cur_user,
  setShowFullRecord,
  filters,
  setFilters,
}) => {
  const isInteractionDisabled = !userHasEntitySearch;
  const [entityReturns, setEntityReturns] = useState<any>({
    isLoading: true,
    data: [],
  });
  const [ownershipReturns, setOwnershipReturns] = useState<any>({
    isLoading: true,
    data: [],
  });
  // Drop Ownership responses that finish after a newer screen started —
  // without this, a slow browse/match_all or prior name can overwrite the
  // current tab count with millions of Hits.
  const ownershipRequestIdRef = useRef(0);
  // The record the last Record ID Search matched. The Ownership tab is queried
  // by its name, so later Ownership pages need it too.
  const recordSubjectRef = useRef<EntityScreenSubject | null>(null);
  const [entityInputMode, setEntityInputMode] =
    useState<EntityScreenInputMode>('details');
  // The query of the last fresh screen: a Run on the same one keeps the
  // screen's hidden rows and recent screen.
  const lastScreenQueryRef = useRef<EntityScreenQuery | null>(null);
  const [screenReason, setScreenReason] =
    useState<EntityScreenRerunReason>('newSubject');
  // Beneficial Ownership AI evaluation result (lifted up from <Search/>) so it
  // can be persisted alongside the ticket when the user saves their work.
  const [beneficialOwnership, setBeneficialOwnership] = useState<any>(null);
  const [adverseMedia, setAdverseMedia] = useState<any>(null);
  const [initialComplete, setInitialComplete] = useState(false);
  const [hasRunOnce, setHasRunOnce] = useState(false);
  const [aiSums, setAiSums] = useState<any>([]);
  const [filterMode, setFilterMode] = useState(false);
  const [appliedFilter, setAppliedFilter] = useState<any>({});
  const [openDate, setOpenDate] = useState(false);
  const [submit, setSubmit] = useState<any>({
    trigger: false,
    reason: '',
  });
  const [entityFiltersTotal, setEntityFiltersTotal] = useState<any>({
    isLoading: true,
    data: 0,
  });
  const [saving, setSaving] = useState(false);
  // Bumped by the top-of-page Clear so the results table fully remounts,
  // wiping its internal state (in-table search, sort, tab, and column
  // filters). The filter button beside the table keeps clearing only the
  // table-level filters on its own.
  const [tableResetSignal, setTableResetSignal] = useState(0);
  const [frequency, setFrequency] = useState('Once');
  const [shareEmail, setShareEmail] = useState<string[]>([]);
  const [ticketNameOpen, setTicketNameOpen] = useState(false);
  const [aiLoading, setAILoading] = useState(false);
  const [newTicketName, setNewTicketName] = useState<string>('');
  const { enqueueSnackbar } = useSnackbar();
  const logAppAction = useLogAppAction();
  
  const dt = new Date();
  const dtWithoutTz = new Date(
    dt.valueOf() + dt.getTimezoneOffset() * 60 * 1000,
  );
  const formatted_dt = format(dtWithoutTz, 'yyyy-MM-dd');
  const getDefaultEntityState = () => ({
    fuzzy: true,
    archived: false,
    date: null,
    endDate: null,
    dateRange: [],
    lenses: [],
    countries: [],
    agencies: [],
    lists: [],
  });

  // FILTER OPTIONS STATE
  // The screen's filters (lens, source country, agency, list) narrow the
  // lists it runs against, as in import and export screening. They live in
  // entityState under the names saved filters and tickets use.
  const screenScope: Scope = {
    ...emptyScope(),
    lens: entityState.lenses || [],
    country: entityState.countries || [],
    agency: entityState.agencies || [],
    list: entityState.lists || [],
  };
  // Every lens → source country → agency → list with its record count: the
  // columns of the Filters modal. Loaded once, unfiltered, with the browse.
  const [listCatalog, setListCatalog] = useState<{
    loading: boolean;
    rows: ListCatalogRow[];
    total: number | null;
  }>({ loading: true, rows: [], total: null });
  // The filters the screen on show ran with, which the verdict boxes, Export
  // and Esc back to it go by; and its Hit Total without them, the M in the
  // verdict boxes' "Filtered: N of M matches".
  const [shownScope, setShownScope] = useState<Scope>(emptyScope);
  const [unfilteredMatchTotal, setUnfilteredMatchTotal] = useState<
    number | null
  >(null);
  const [lensOpts, setLensOpts] = useState<{
    isLoading: boolean;
    data: any;
  }>({ isLoading: true, data: [] });
  const [countryOpts, setCountryOpts] = useState<{
    isLoading: boolean;
    data: any;
  }>({ isLoading: true, data: [] });
  const [agencyOpts, setAgencyOpts] = useState<{
    isLoading: boolean;
    data: any;
  }>({ isLoading: true, data: [] });
  const [listOpts, setListOpts] = useState<{
    isLoading: boolean;
    data: any;
  }>({ isLoading: true, data: [] });

  const [countOpts, setCountOpts] = useState([]);
  const [agOpts, setAgOpts] = useState([]);

  if (!cur_user.selected_team) {
    return <Redirect to='/' />;
  }

  const handleClear = () => {
    if (isInteractionDisabled) {
      return;
    }
    setEntityState({
      fuzzy: true,
      archived: false,
      date: null,
      dateArr: [],
      lenses: [],
      agencies: [],
      countries: [],
      lists: [],
    });
    setSubmit({
      reason: 'apply',
      trigger: !submit.trigger,
      location: 'Entity',
    });
    setAppliedFilter({});
  };

  const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isInteractionDisabled) {
      return;
    }
    setEntityState({ ...entityState, archived: event.target.checked });
  };

  // const handleDateChange = (newValue: any) => {
  //   if (isInteractionDisabled) {
  //     return;
  //   }
  //   const arr: any = [...newValue];
  //   if (newValue[0] !== null) {
  //     arr.forEach((dt: any, index: number) => {
  //       if (dt !== null) {
  //         const dtWithoutTz = new Date(
  //           dt.valueOf() + dt.getTimezoneOffset() * 60 * 1000,
  //         );
  //         const formatted_dt = format(dtWithoutTz, 'yyyy-MM-dd');
  //         arr[index] = formatted_dt;
  //       }
  //     });
  //     setEntityState({ ...entityState, date: newValue, dateRange: arr });
  //   } else {
  //     setEntityState({ ...entityState, date: null, dateRange: [] });
  //   }
  // };

  const handleDateChangeNew = (startDate: any, endDate: any) => {
    if (isInteractionDisabled) {
      return;
    }

    const arr: any = [];
    if (startDate) {
      const dtWithoutTzLocal = new Date(
        startDate.valueOf() + startDate.getTimezoneOffset() * 60 * 1000,
      );
      const formatted_dtLocal = format(dtWithoutTzLocal, 'yyyy-MM-dd');
      arr.push(formatted_dtLocal);
    }
    if (endDate !== null) {
      const dtWithoutTzLocal = new Date(
        endDate.valueOf() + endDate.getTimezoneOffset() * 60 * 1000,
      );
      const formatted_dtLocal = format(dtWithoutTzLocal, 'yyyy-MM-dd');
      arr.push(formatted_dtLocal);
      setEntityState({
        ...entityState,
        date: startDate,
        endDate: endDate,
        dateRange: arr,
      });
    } else {
      setEntityState({
        ...entityState,
        date: startDate,
        endDate: null,
        dateRange: [],
      });
    }
  };

  // Strip wrapping single/double/smart quotes so the entity name shows up
  // plainly in AI summaries (the LLM otherwise echoes the quotes back in its
  // rationale, e.g. "'Acme Corp' was found on...").
  const stripWrappingQuotes = (input: string) => {
    if (!input) {
      return '';
    }
    let out = input.trim();
    const quotePairs: Array<[string, string]> = [
      ['"', '"'],
      ["'", "'"],
      ['“', '”'],
      ['‘', '’'],
      ['`', '`'],
    ];
    let changed = true;
    while (changed) {
      changed = false;
      for (const [open, close] of quotePairs) {
        if (out.length >= 2 && out.startsWith(open) && out.endsWith(close)) {
          out = out.slice(open.length, out.length - close.length).trim();
          changed = true;
          break;
        }
      }
    }
    return out;
  };

  const cleanCompanyName = (input: string) => {
    if (!input) {
      return '';
    }

    // List of suffixes to strip (case-insensitive, allow optional punctuation/whitespace)
    const suffixes = [
      'inc',
      'inc\\.',
      'incorporated',
      'corp',
      'corp\\.',
      'corporation',
      'co',
      'co\\.',
      'company',
      'ltd',
      'ltd\\.',
      'limited',
      'llc',
      'llp',
      'plc',
      'gmbh',
      's\\.a\\.',
      's\\.a\\.s',
      's\\.a\\.r\\.l',
      'bv',
      'nv',
      'oy',
      'ab',
      'pte',
      'pty',
      'sarl',
    ];

    // Build regex to match any suffix at the end
    const regex = new RegExp(`\\b(?:${suffixes.join('|')})\\b\\.?$`, 'i');

    // Remove suffixes iteratively in case of multiple (e.g. "Company LLC Inc")
    let cleaned = input.trim();
    while (regex.test(cleaned)) {
      cleaned = cleaned.replace(regex, '').trim();
    }

    return cleaned;
  };

  // BITE VAULT TICKET
  const handleTicket = async (
    values: any,
    recipients: string[] = shareEmail,
  ): Promise<boolean> => {
    if (isInteractionDisabled) {
      return false;
    }
    const new_case = {
      groupId: cur_user.selected_team.id,
      ...values.basics,
      ...values,
    };
    const resp = await CasesAPI.createCase({ newCase: new_case });
    if (resp?.success) {
      recipients.forEach((email) => {
        // Call to notification sending function here
        handleShareTicketNotification(
          values.ticketName,
          cur_user,
          email,
          resp.result._id,
        );
      });
      setSaving(false);
      setTicketNameOpen(false);
      setNewTicketName('');
      enqueueSnackbar('Successfully created new case', { variant: 'success' });
      return true;
    }
    setSaving(false);
    setTicketNameOpen(false);
    if (resp) {
      const error_msg =
        'Error saving new case. Uploaded file(s) may be too large.';
      enqueueSnackbar(error_msg, { variant: 'error' });
    }
    return false;
  };

  const compressData = (data: any) => {
    const stringifiedData = JSON.stringify(data);
    const compressed = pako.gzip(stringifiedData);
    return Buffer.from(compressed).toString('base64'); // Convert to base64 for storage or transmission
  };

  const createTicket = async (
    name: string,
    recipients: string[] = shareEmail,
    ticketFrequency: string = frequency,
  ): Promise<boolean> => {
    if (isInteractionDisabled) {
      return false;
    }
    setSaving(true);
    const entFilterQuery = {
      lenses: entityState.lenses,
      countries: entityState.countries,
      agencies: entityState.agencies,
      lists: entityState.lists,
      entRes: true,
      filterDate: entityState.dateRange,
    };

    const comFilterQuery = {
      lenses: [],
      countries: [],
      agencies: [],
      lists: [],
      hsCodeLists: [],
      sections: [],
      chapters: [],
      codes: [],
      entRes: false,
      filterDate: [],
    };

    const ownerQuery = {
      kc_id: cur_user.basics?.id,
      email: cur_user.basics?.email,
      firstName: cur_user.basics?.firstName,
      lastName: cur_user.basics?.lastName,
    };

    const query: any = {
      ticketName: name,
      lastUpdate: formatted_dt,
      entity: {
        primary: inputs.primary,
        country: inputs.country,
        address: inputs.address,
      },
      docType: 'Person / Company',
      entityFilter: entFilterQuery,
      commodityFilter: comFilterQuery,
      frequency: ticketFrequency,
      sharedWith: recipients,
      aiSums: aiSums,
      beneficialOwnership: beneficialOwnership || null,
      adverseMedia: adverseMedia || null,
      activeFilter: '',
      selectedOpts: [],
      status: 'Complete',
      favorite: false,
      // Hide-hit is a BITE DB record action. Screening no longer collects
      // hidden rows, so new tickets do not carry a removed-hit list.
      removedHits: [],
      groupName: cur_user.selected_team.name,
      template: [],
      colMapping: [],
      activity: {
        user: {
          kc_id: cur_user.basics?.id,
          email: cur_user.basics?.email,
          firstName: cur_user.basics?.firstName,
          lastName: cur_user.basics?.lastName,
        },
        action: 'saved work',
        timeStamp: Date.now(),
      },
      owner: ownerQuery,
    };
    const extractSources = (docs: any): any[] => {
      return docs.map((doc: any) => doc._source);
    };
    // Tolerate a zero-result screen: a completed search still yields a
    // response object with total 0 and an empty hits array, so the ticket
    // saves cleanly with no matches.
    const entityHitsContainer = entityReturns?.data?.[0]?.result?.body?.hits;
    const length = entityHitsContainer?.total?.value || 0;

    const structuredObj = [
      {
        dataSet: 'Entities',
        docs: extractSources(entityHitsContainer?.hits || []),
        field: inputs.primary,
        hits: length,
        row: 1,
      },
    ];

    // Persist the Ownership tab (BOI/PEP) matches so the saved ticket can
    // render the same Ownership results the live tool showed. Stored compressed
    // (like the entity results) under a top-level field on the ticket.
    const ownershipHits =
      ownershipReturns?.data?.[0]?.result?.body?.hits?.hits || [];
    const ownershipSources = extractSources(ownershipHits);
    const ownershipTotal =
      ownershipReturns?.data?.[0]?.result?.body?.hits?.total?.value ??
      ownershipSources.length;
    const ownershipResults = compressData({
      docs: ownershipSources,
      total: ownershipTotal,
    });

    const initialRows: any = [];
    if (typeof inputs.primary === 'string') {
      initialRows.push([
        {
          type: 'entity',
          searchVal: inputs.primary,
          row: 1,
        },
      ]);
    } else if (Array.isArray(inputs.primary)) {
      inputs.primary.forEach((element: string, index: number) => {
        initialRows.push([
          {
            type: 'entity',
            searchVal: element,
            row: index + 1,
          },
        ]);
      });
    }

    const ticketQuery = {
      ...query,
      curTotal: length,
      curNew: 0,
      ownershipResults: ownershipResults,
      results: {
        allResults: compressData(structuredObj),
        allTotal: length,
        newResults: [],
        newTotal: 0,
        date: formatted_dt,
        docInfo: initialRows,
      },
    };
    return handleTicket(ticketQuery, recipients);
  };

  // HANDLE CSV RESULTS WITH 200 RESULTS
  const getEntCSVResults = async (
    pageNum: number,
    exportContext?: EntityTableExportContext,
  ) => {
    if (isInteractionDisabled) {
      return [];
    }
    const allResults: any = [];
    const exportSearch = exportContext?.searchQuery.trim() || '';
    const exportOwnership = exportContext?.activeTab === 'Ownership';
    const exportRecord = entityInputMode === 'record' && !exportSearch;
    const entityPrimary = exportRecord
      ? ''
      : cleanCompanyName(exportSearch || inputs.primary || '');
    const ownershipPrimary = cleanCompanyName(
      exportSearch ||
        (entityInputMode === 'record'
          ? recordSubjectRef.current?.name || ''
          : inputs.primary || ''),
    );
    const tableFilters = buildEntityTableFilterParams(
      exportContext?.tableFilters,
    );
    const query: any = {
      primary: exportOwnership ? ownershipPrimary : entityPrimary,
      // An unfiltered Record ID screen must export the exact record that was
      // screened.  The prior request only sent `primary`, which made the CSV
      // fall back to a name search (or to every record when it was blank).
      record: exportOwnership || !exportRecord ? '' : inputs.record,
      // the export is of the screen on show, so it ran with these filters
      ...(exportOwnership ? {} : listScopeParams(shownScope)),
      dateRange: entityState.dateRange,
      pageNum: pageNum,
      agg: true,
      archived: entityState.archived,
      qty: 200,
      ...(tableFilters ? { tableFilters: tableFilters } : {}),
    };
    const res = exportOwnership
      ? await EntityServiceAPI.ownershipSearch(query)
      : await EntityServiceAPI.entitySearch(query);
    if (res?.success) {
      allResults.push(res?.result);
    }
    return allResults;
  };

  // GET INITIAL RESULTS AND SET OPTIONS
  const initialAPICall = async (
    syncInitialLenses = entityState.lenses.length > 0,
  ) => {
    if (isInteractionDisabled) {
      return;
    }
    const browsePlan = planInteractiveToolHitsFetch({
      isFreshScreen: false,
      isInitialBrowse: true,
      pageNum: 0,
    });
    if (browsePlan.ownershipPageNum !== null) {
      getOwnershipResults(browsePlan.ownershipPageNum);
    } else {
      setOwnershipReturns({ isLoading: false, data: [] });
    }
    const tableQuery: any = {
      agg: false,
    };
    const tableRes = await EntityServiceAPI.entitySearch(tableQuery);
    if (tableRes?.success) {
      // Browse/default load stays server-paged (100/page), so `complete` is
      // false — the table only switches to client-side once a search runs.
      setEntityReturns({
        isLoading: false,
        data: [tableRes.result],
        complete: false,
      });
    } else {
      // A failed initial browse must still clear isLoading — canRun gates
      // "Run screening" on !results.isLoading, so leaving this stuck at its
      // initial true would permanently disable running a fresh screen after
      // nothing more than a flaky background fetch.
      setEntityReturns({ isLoading: false, data: [], complete: false });
    }

    const aggQuery: any = {
      agg: true,
    };
    const aggRes = await EntityServiceAPI.entitySearch(aggQuery);
    const aggBody = aggRes?.success ? aggRes.result?.result?.body : null;
    setListCatalog({
      loading: false,
      rows: flattenListAggs(aggBody?.aggregations, 'entityAggs'),
      total:
        typeof aggBody?.hits?.total?.value === 'number'
          ? aggBody.hits.total.value
          : null,
    });
    if (aggRes?.success) {
      setEntityFiltersTotal({
        isLoading: false,
        data: aggRes.result?.result?.body?.hits?.total?.value,
      });

      setLensOpts({
        isLoading: false,
        data: [...aggRes.result.result.body.aggregations.entityAggs.buckets],
      });
      const countryBuckets: any = [];
      aggRes.result?.result?.body?.aggregations?.entityAggs?.buckets.map(
        (bucket: any) => {
          countryBuckets.push(bucket.countryAgg.buckets);
        },
      );

      setCountryOpts({ isLoading: false, data: countryBuckets.flat() });
      const agencyBuckets: any = [];
      aggRes.result?.result?.body?.aggregations?.entityAggs?.buckets.map(
        (bucket: any) => {
          bucket.countryAgg.buckets.map((secondBucket: any) => {
            agencyBuckets.push(secondBucket.agencyAgg.buckets);
          });
        },
      );

      setAgencyOpts({ isLoading: false, data: agencyBuckets.flat() });
      const listBuckets: any = [];
      aggRes.result?.result?.body?.aggregations?.entityAggs?.buckets.map(
        (bucket: any) => {
          bucket.countryAgg.buckets.map((secondBucket: any) => {
            secondBucket.agencyAgg.buckets.map((thirdBucket: any) => {
              listBuckets.push(thirdBucket.listNameAgg.buckets);
            });
          });
        },
      );

      setListOpts({ isLoading: false, data: listBuckets.flat() });
      if (syncInitialLenses) {
        setInitialComplete(true);
      }
    }
  };

  const handleFullReset = async () => {
    if (isInteractionDisabled) {
      return;
    }

    setInputs({
      primary: '',
      country: '',
      address: '',
      record: '',
    });
    setEntityState(getDefaultEntityState());
    setAppliedFilter({});
    setFilterMode(false);
    setOpenDate(false);
    setPage(0);
    setAiSums([]);
    setBeneficialOwnership(null);
    setAdverseMedia(null);
    setAILoading(false);
    setInitialComplete(false);
    setHasRunOnce(false);
    setEntityFiltersTotal({ isLoading: true, data: 0 });
    setEntityReturns({ isLoading: true, data: [] });
    ownershipRequestIdRef.current += 1;
    setOwnershipReturns({ isLoading: true, data: [] });
    lastScreenQueryRef.current = null;
    setShownScope(emptyScope());
    setTableResetSignal((prev) => prev + 1);
    await initialAPICall(false);
  };

  // HANDLE LENS CHANGE
  const handleLensChange = async () => {
    if (isInteractionDisabled) {
      return;
    }
    setLensOpts((prev) => ({ ...prev, isLoading: false }));
    setCountryOpts((prev) => ({ ...prev, isLoading: true }));
    setAgencyOpts((prev) => ({ ...prev, isLoading: true }));
    setListOpts((prev) => ({ ...prev, isLoading: true }));
    const query: any = {
      lens: entityState.lenses,
      updateDay: entityState.dateArr,
      agg: true,
    };
    try {
      const res = await EntityServiceAPI.entitySearch(query);
      if (res?.success && entityState.lenses.length) {
        const addedBuckets: any = [];
        res?.result?.result?.body?.aggregations?.entityAggs?.buckets.map(
          (bucket: any) => {
            addedBuckets.push(bucket.countryAgg.buckets);
          },
        );
        const selections = addedBuckets.flat().map((obj: any) => {
          return obj.key;
        });
        setEntityState({
          ...entityState,
          countries: selections,
        });
        setCountOpts(selections);
      } else if (entityState.lenses.length === 0) {
        setEntityState({
          ...entityState,
          countries: [],
          agencies: [],
          lists: [],
        });
        setCountryOpts((prev) => ({ ...prev, isLoading: false }));
        setAgencyOpts((prev) => ({ ...prev, isLoading: false }));
        setListOpts((prev) => ({ ...prev, isLoading: false }));
      }
      setSubmit({
        trigger: !submit.trigger,
        reason: 'Listing Country',
      });
    } catch (_error) {
      setCountryOpts((prev) => ({ ...prev, isLoading: false }));
      setAgencyOpts((prev) => ({ ...prev, isLoading: false }));
      setListOpts((prev) => ({ ...prev, isLoading: false }));
    } finally {
      setLensOpts((prev) => ({ ...prev, isLoading: false }));
    }
  };

  // HANDLE COUNTRY CHANGE
  const handleCountryChange = async () => {
    if (isInteractionDisabled) {
      return;
    }
    setAgencyOpts({ ...agencyOpts, isLoading: true });
    setListOpts({ ...listOpts, isLoading: true });
    const query: any = {
      lens: entityState.lenses,
      listCountry: entityState.countries,
      updateDay: entityState.dateArr,
      agg: true,
    };
    const res = await EntityServiceAPI.entitySearch(query);
    if (res?.success && entityState.countries.length > 0) {
      const addedBuckets: any = [];
      res?.result?.result?.body?.aggregations?.entityAggs?.buckets.map(
        (bucket: any) => {
          bucket.countryAgg.buckets.map((secondBucket: any) => {
            addedBuckets.push(secondBucket.agencyAgg.buckets);
          });
        },
      );
      const selections = addedBuckets.flat().map((obj: any) => {
        return obj.key;
      });
      setEntityState({
        ...entityState,
        agencies: selections,
      });
      setAgOpts(selections);
    } else if (entityState.countries.length === 0) {
      setEntityState({
        ...entityState,
        agencies: [],
        lists: [],
      });
    }
    setSubmit({
      trigger: !submit.trigger,
      reason: 'Agency',
    });
    setCountryOpts({ ...countryOpts, isLoading: false });
  };

  // HANDLE AGENCY CHANGE
  const handleAgencyChange = async () => {
    if (isInteractionDisabled) {
      return;
    }
    setListOpts({ ...listOpts, isLoading: true });
    const query: any = {
      lens: entityState.lenses,
      agency: entityState.agencies,
      updateDay: entityState.dateArr,
      agg: true,
    };
    const res = await EntityServiceAPI.entitySearch(query);
    if (res?.success && entityState.agencies.length > 0) {
      const addedBuckets: any = [];
      res?.result?.result?.body?.aggregations?.entityAggs?.buckets.map(
        (bucket: any) => {
          bucket.countryAgg.buckets.map((secondBucket: any) => {
            secondBucket.agencyAgg.buckets.map((thirdBucket: any) => {
              addedBuckets.push(thirdBucket.listNameAgg.buckets);
            });
          });
        },
      );
      const selections = addedBuckets.flat().map((obj: any) => {
        return obj.key;
      });
      setEntityState({
        ...entityState,
        lists: selections,
      });
    } else if (entityState.agencies.length === 0) {
      setEntityState({
        ...entityState,
        lists: [],
      });
    }
    setSubmit({
      trigger: !submit.trigger,
      reason: 'List',
    });
    setAgencyOpts({ ...agencyOpts, isLoading: false });
  };

  // HANDLE LIST CHANGE
  const handleListChange = async () => {
    if (isInteractionDisabled) {
      return;
    }
    const query: any = {
      lens: entityState.lenses,
      listName: entityState.lists,
      updateDay: entityState.dateArr,
      agg: true,
    };
    const res = await EntityServiceAPI.entitySearch(query);
    if (res?.success) {
      setEntityFiltersTotal({
        isLoading: false,
        data: res?.result?.result?.body?.hits?.total?.value,
      });
    }
    setListOpts({ ...listOpts, isLoading: false });
  };

  // The name a screen request searches: the in-table search when one is
  // active, else the typed name.
  const screenPrimary = (searchOverride?: string): string =>
    searchOverride !== undefined ? searchOverride.trim() : inputs.primary;

  // BENEFICIAL OWNERSHIP / PEP DATA
  // Pulls matches from the bite_app_entities_boi_and_peps index for the
  // Ownership tab. Runs alongside the entity screen using the same searched
  // name/country so the tab and the Beneficial Ownership agent share one
  // result set. A Record ID Search passes the matched record as
  // `recordSubject`: the tab is queried by its name, without the ID. The
  // screen's filters pick sanctions and watch lists, so they never narrow it.
  const getOwnershipResults = async (
    pageNum: number,
    options: {
      searchOverride?: string;
      recordSubject?: EntityScreenSubject | null;
    } = {},
  ) => {
    if (isInteractionDisabled) {
      setOwnershipReturns({ isLoading: false, data: [] });
      return;
    }
    const { recordSubject } = options;
    const effectivePrimary = recordSubject
      ? recordSubject.name
      : screenPrimary(options.searchOverride);
    const requestId = ++ownershipRequestIdRef.current;
    // Clear prior Hits immediately so a loading tab cannot keep showing a
    // stale multi-million browse total (or the previous entity's count).
    setOwnershipReturns({ isLoading: true, data: [] });
    const query = buildInteractiveEntityHitsScoreParams({
      base: {
        primary: cleanCompanyName(effectivePrimary),
        record: recordSubject ? '' : inputs.record,
        country: inputs.country,
        dateRange: entityState.dateRange,
        archived: entityState.archived,
        agg: true,
      },
      pageNum: pageNum,
    });
    const res = await EntityServiceAPI.ownershipSearch(query);
    if (requestId !== ownershipRequestIdRef.current) {
      return;
    }
    if (res?.success) {
      setOwnershipReturns({ isLoading: false, data: [res.result] });
    } else {
      setOwnershipReturns({ isLoading: false, data: [] });
    }
  };

  // Record ID Search: the subject is the matched record, never the ID. Returns
  // the AI Evaluation summaries; none when no record matched.
  const screenMatchedRecord = async (args: {
    res: any;
    ownershipRequestId: number;
    isRestricted: boolean;
  }): Promise<any[]> => {
    const recordPlan = planInteractiveEntityAiEvaluation({
      hasPrimary: false,
      inputMode: 'record',
      matchedRecord: args.res?.success ? topHitSource(args.res.result) : null,
    });
    if (ownershipRequestIdRef.current === args.ownershipRequestId) {
      if (recordPlan.kind === 'record') {
        recordSubjectRef.current = recordPlan.subject;
        getOwnershipResults(0, { recordSubject: recordPlan.subject });
      } else {
        setOwnershipReturns({ isLoading: false, data: [] });
      }
    }
    if (recordPlan.kind !== 'record' || args.isRestricted) {
      return [];
    }
    const filtered = await includeFields(ENTITY_AI_FIELDS, recordPlan.sources);
    const ai_summary = await runAIEvaluationSingleEntity(
      recordPlan.subject.name,
      recordPlan.subject.country || inputs.country,
      inputs.address,
      JSON.stringify(filtered),
    );
    if (ai_summary) {
      ai_summary.entity_name = recordPlan.subject.name;
      ai_summary.screenDate = new Date().toLocaleDateString();
    }
    return [ai_summary];
  };

  // API CALL THAT TIES IN THE FILTERS AND THE INPUT
  // ONLY CALL THAT WILL AFFECT THE RESULTS TABLE
  const handleSubmit = async (
    pageNum: number,
    searchOverride?: string,
    // `scope` comes straight from the Filters modal: state set in the same
    // tick is not readable here yet.
    runOptions: EntityScreenRunOptions = {},
  ) => {
    const { runAiEvaluation = true, dataset = 'both' } = runOptions;
    if (isInteractionDisabled) {
      setEntityReturns({ isLoading: false, data: [] });
      setAILoading(false);
      return;
    }
    // True Pagination: one server page per request. Fresh screens start at
    // page 0; pager changes pass the requested page for the active tab only.
    // AI uses a separate top-N sample request, started with the table page
    // so evaluation does not wait on Hits. Never a table-driving 1k bulk.
    const fetchPlan = planInteractiveToolHitsFetch({
      isFreshScreen: runAiEvaluation,
      dataset: dataset,
      pageNum: pageNum,
    });
    const screenQuery: EntityScreenQuery = {
      inputMode: entityInputMode,
      query: String(
        (entityInputMode === 'record' ? inputs.record : inputs.primary) || '',
      ).trim(),
    };
    const reason = resolveEntityScreenRerunReason({
      requested: runOptions.reason,
      current: screenQuery,
      last: lastScreenQueryRef.current,
    });
    // A Record ID Search looks up one exact record, so the filters don't
    // apply to it any more than country and address do.
    const scope =
      entityInputMode === 'record'
        ? emptyScope()
        : runOptions.scope ?? screenScope;
    const isFiltered = countScope(scope, false) > 0;
    if (runAiEvaluation) {
      // A fresh screen (the Run button, Enter-key submit, or the initial
      // page-load run) — as opposed to a pagination page-change or lightweight
      // re-fetch, which call this with runAiEvaluation=false (see
      // EntityScreeningResultsTable.tsx) and shouldn't count as a new "run".
      logAppAction('entity_screening:run');
      setHasRunOnce(true);
      setPage(0);
      setEntityReturns({ isLoading: true, data: [] });
      setOwnershipReturns({ isLoading: true, data: [] });
      setAILoading(true);
      setAiSums([]);
      lastScreenQueryRef.current = screenQuery;
      setScreenReason(reason);
      setShownScope(scope);
    } else if (fetchPlan.entityPageNum !== null) {
      setEntityReturns((prev: any) => ({ ...prev, isLoading: true }));
    }

    const effectivePrimary = screenPrimary(searchOverride);
    // An in-table search queries by its own text, in record mode too.
    const isRecordSearch =
      entityInputMode === 'record' && searchOverride === undefined;
    if (isRecordSearch && runAiEvaluation) {
      // Who a Record ID Search is about is only known once the table search
      // returns the matched record; the Ownership tab is queried then.
      recordSubjectRef.current = null;
      ownershipRequestIdRef.current += 1;
    } else if (fetchPlan.ownershipPageNum !== null) {
      const recordSubject = isRecordSearch ? recordSubjectRef.current : null;
      if (isRecordSearch && !recordSubject) {
        setOwnershipReturns({ isLoading: false, data: [] });
      } else {
        getOwnershipResults(fetchPlan.ownershipPageNum, {
          searchOverride: searchOverride,
          recordSubject: recordSubject,
        });
      }
    }
    const recordOwnershipRequestId = ownershipRequestIdRef.current;

    if (fetchPlan.entityPageNum === null) {
      return;
    }

    let total = 0;
    const aiSummaries: any = [];
    const hitsQueryBase = {
      primary: cleanCompanyName(effectivePrimary),
      record: inputs.record,
      country: inputs.country,
      address: inputs.address,
      // The screen runs against its filters only. The verdict boxes say so
      // ("Filtered: N of M matches"), so a filtered screen never reads as a
      // clean one.
      ...listScopeParams(scope),
      dateRange: entityState.dateRange,
      archived: entityState.archived,
      agg: true,
    };

    const query = buildInteractiveEntityHitsScoreParams({
      base: hitsQueryBase,
      pageNum: fetchPlan.entityPageNum,
    });

    const shouldRunAi = runAiEvaluation && effectivePrimary.length > 0;
    const aiPlan = planInteractiveEntityAiEvaluation({
      hasPrimary: effectivePrimary.length > 0,
      inputMode: isRecordSearch ? 'record' : 'details',
    });
    const isRestricted = cur_user?.role === 'domain-restricted';
    const cleanedPrimary = stripWrappingQuotes(effectivePrimary);
    const shouldSampleAi =
      shouldRunAi && aiPlan.kind === 'sample' && !isRestricted;

    // Table page and AI sample are independent OpenSearch calls. Start the
    // sample with the table request so pyapi does not wait on page-1 latency.
    // Await the table first so Hits can render while the 250-doc sample
    // (and then pyapi) continue.
    const tableSearch = EntityServiceAPI.entitySearch(query);
    const sampleSearch = shouldSampleAi
      ? EntityServiceAPI.entitySearch(
          buildInteractiveEntityAiSampleScoreParams({ base: hitsQueryBase }),
        )
      : null;
    const matchTotalSearch =
      runAiEvaluation && isFiltered
        ? EntityServiceAPI.entitySearch(
            buildInteractiveEntityMatchTotalScoreParams({
              base: { ...hitsQueryBase, ...listScopeParams(emptyScope()) },
            }),
          )
        : null;

    const [res, matchTotalRes] = await Promise.all([
      tableSearch,
      matchTotalSearch,
    ]);
    if (runAiEvaluation) {
      const unfilteredSource = matchTotalSearch ? matchTotalRes : res;
      setUnfilteredMatchTotal(
        unfilteredSource?.success
          ? unfilteredSource?.result?.result?.body?.hits?.total?.value || 0
          : null,
      );
    }
    if (res?.success) {
      total = total + (res?.result?.result?.body?.hits?.total?.value || 0);
      setEntityReturns({
        isLoading: false,
        data: [res.result],
        // Never mark complete — that switched the table to client-side paging
        // over a bulk-loaded set. Hits stay server-paged from Hit Total.
        complete: false,
      });
    } else if (runAiEvaluation) {
      setEntityReturns({ isLoading: false, data: [], complete: false });
    }

    if (isRecordSearch && runAiEvaluation) {
      aiSummaries.push(
        ...(await screenMatchedRecord({
          res: res,
          ownershipRequestId: recordOwnershipRequestId,
          isRestricted: isRestricted,
        })),
      );
    }

    if (shouldSampleAi && sampleSearch) {
      const sampleRes = await sampleSearch;
      if (!sampleRes?.success) {
        aiSummaries.push({
          best_match: '',
          entity_name: cleanedPrimary,
          risk: 'Unknown',
          match_probability: 0,
          explanation:
            'This entity could not be evaluated due to a processing error, so no risk level was assigned. Please try running the screening again.',
          screenDate: new Date().toLocaleDateString(),
        });
      } else {
        const aiHits = sampleRes?.result?.result?.body?.hits?.hits ?? [];
        const hit_list = aiHits.map((hit: any) => hit._source);
        const eligible = selectInteractiveEntityAiSources({
          searchTerm: cleanedPrimary,
          sources: hit_list,
        });
        if (eligible.length === 0) {
          aiSummaries.push({
            best_match: '',
            entity_name: cleanedPrimary,
            explanation: ENTITY_AI_EVAL_NO_STRONG_MATCHES_MESSAGE,
            match_probability: '',
            risk: '',
            screenDate: new Date().toLocaleDateString(),
          });
        } else {
          const filtered = await includeFields(ENTITY_AI_FIELDS, eligible);
          const ai_summary = await runAIEvaluationSingleEntity(
            cleanedPrimary,
            inputs.country,
            inputs.address,
            JSON.stringify(filtered),
          );
          if (ai_summary) {
            ai_summary.entity_name = cleanedPrimary;
            ai_summary.screenDate = new Date().toLocaleDateString();
          }
          aiSummaries.push(ai_summary);
        }
      }
    }

    if (runAiEvaluation) {
      setAiSums(aiSummaries);
      setAILoading(false);
    }
    if (
      entityState.lenses.length === 0 &&
      entityState.countries.length === 0 &&
      entityState.agencies.length === 0 &&
      entityState.lists.length === 0
    ) {
      setEntityFiltersTotal({ isLoading: false, data: total });
    }
  };

  // Sets the screen's filters. `rerun` re-runs the screen and its AI
  // Evaluation with them, never Adverse Media or Beneficial Ownership: the
  // filters pick lists, not the news or the Ownership tab.
  const applyScreenFilters = (next: Scope, rerun: boolean) => {
    setEntityState({
      ...entityState,
      lenses: next.lens,
      countries: next.country,
      agencies: next.agency,
      lists: next.list,
    });
    if (rerun) {
      setPage(0);
      handleSubmit(0, undefined, { reason: 'screenFilters', scope: next });
    }
  };

  // Saved filters are the team's `entity` search filters, saved from the
  // Filters modal.
  const createSavedScreenFilter = async (
    kind: SavedFilterKind,
    name: string,
    scope: Scope,
  ): Promise<SavedFilter | null> => {
    const res: any = await DataSearchAPI.createSearchFilter({
      newSearchFilter: {
        userId: cur_user?.basics?.id,
        groupId: cur_user?.selected_team?.id,
        filterName: name,
        type: kind,
        ...savedFields(scope, kind),
        entRes: true,
      },
    });
    if (res?.success) {
      setFilters((prev: any) => ({
        isLoaded: true,
        arr: [...(prev?.arr || []), res.result],
      }));
      enqueueSnackbar('Saved your filter', { variant: 'success' });
      return res.result as SavedFilter;
    }
    enqueueSnackbar('Could not save the filter. Please try again.', {
      variant: 'error',
    });
    return null;
  };

  const updateSavedScreenFilter = async (filter: SavedFilter, scope: Scope) => {
    const res: any = await DataSearchAPI.updateSearchFilter({
      update: { ...filter, ...savedFields(scope, filter.type), entRes: true },
    });
    if (res?.success) {
      setFilters((prev: any) => ({
        isLoaded: true,
        arr: (prev?.arr || []).map((item: any) =>
          item._id === res.result._id ? res.result : item,
        ),
      }));
      enqueueSnackbar(`Updated “${filter.filterName}”`, { variant: 'success' });
    } else {
      enqueueSnackbar('Could not update the filter. Please try again.', {
        variant: 'error',
      });
    }
  };

  const deleteSavedScreenFilter = async (filter: SavedFilter) => {
    const res: any = await DataSearchAPI.deleteSearchFilter(filter._id);
    if (res?.success) {
      setFilters((prev: any) => ({
        isLoaded: true,
        arr: (prev?.arr || []).filter((item: any) => item._id !== filter._id),
      }));
    } else {
      enqueueSnackbar('Could not delete the filter. Please try again.', {
        variant: 'error',
      });
    }
  };

  const handleMultiPageChange = async (
    input: any,
    pageNum: number,
    i: number,
  ) => {
    if (isInteractionDisabled) {
      return;
    }
    const allResults: any = entityReturns.data;
    const query: any = {
      primary: cleanCompanyName(input),
      country: inputs.country,
      lens:
        entityState.countries.length > 0 ||
        entityState.agencies.length > 0 ||
        entityState.lists.length > 0
          ? []
          : entityState.lenses,
      listCountry:
        entityState.agencies.length > 0 || entityState.lists.length > 0
          ? []
          : entityState.countries,
      agency: entityState.lists.length > 0 ? [] : entityState.agencies,
      listName: entityState.lists,
      dateRange: entityState.dateRange,
      pageNum: pageNum,
      archived: entityState.archived,
      agg: true,
    };
    const res = await EntityServiceAPI.entitySearch(query);
    if (res?.success) {
      allResults[i] = res?.result;
    }
    setEntityReturns({ isLoading: false, data: allResults });
  };

  useEffect(() => {
    if (isInteractionDisabled) {
      return;
    }
    switch (submit.reason) {
      case 'Lens':
        handleLensChange();
        break;
      case 'Listing Country':
        handleCountryChange();
        break;
      case 'Agency':
        handleAgencyChange();
        break;
      case 'List':
        handleListChange();
        break;
      case 'apply':
        handleSubmit(page, undefined, { reason: 'asOfDate' });
        break;
      default:
        initialAPICall();
    }
  }, [submit]);

  useEffect(() => {
    if (isInteractionDisabled) {
      return;
    }
    if (initialComplete) {
      // Lenses handed over in location state arrive as the screen's Lens
      // filter, as they do in import and export screening.
      handleSubmit(page, undefined, { reason: 'newSubject' });
    }
  }, [initialComplete]);

  useEffect(() => {
    setPage(0);
  }, [inputs]);

  useEffect(() => {
    if (isInteractionDisabled) {
      setEntityReturns({ isLoading: false, data: [] });
      setOwnershipReturns({ isLoading: false, data: [] });
      setAILoading(false);
      setAiSums([]);
      setBeneficialOwnership(null);
      setAdverseMedia(null);
    }
  }, [isInteractionDisabled]);

  return (
    <div className='screening-body-container'>
      <Search
        cyberTab={undefined}
        handleTabSwitch={undefined}
        userHasSearch={userHasEntitySearch}
        setShowFullRecord={setShowFullRecord}
        type={'Entities'}
        handleSubmit={handleSubmit}
        inputs={inputs}
        setInputs={setInputs}
        results={entityReturns}
        ownershipResults={ownershipReturns}
        page={page}
        setPage={setPage}
        cyberHeaders={[]}
        getCSVResults={getEntCSVResults}
        handleMultiPageChange={handleMultiPageChange}
        aiSums={aiSums}
        state={entityState}
        setEntityFilterState={setEntityState}
        entityFilterOptions={{
          lensOpts: lensOpts,
          countryOpts: countryOpts,
          agencyOpts: agencyOpts,
          listOpts: listOpts,
        }}
        entityScreenFilters={{
          scope: screenScope,
          shownScope: shownScope,
          listRows: listCatalog.rows,
          loading: listCatalog.loading,
          recordTotal: listCatalog.total,
          savedFilters: filters?.arr || [],
          onCreateSaved: createSavedScreenFilter,
          onUpdateSaved: updateSavedScreenFilter,
          onDeleteSaved: deleteSavedScreenFilter,
          onApply: applyScreenFilters,
        }}
        unfilteredMatchTotal={unfilteredMatchTotal}
        entityFilterSubmit={submit}
        setEntityFilterSubmit={setSubmit}
        filters={filters}
        setFilters={setFilters}
        appliedFilter={appliedFilter}
        setAppliedFilter={setAppliedFilter}
        openFilters={() => setFilterMode(true)}
        handleClear={handleClear}
        handleFullReset={handleFullReset}
        handleOpenDate={() => setOpenDate(true)}
        handleSwitchChange={handleSwitchChange}
        createTicket={createTicket}
        saving={saving}
        shareEmail={shareEmail}
        setShareEmail={setShareEmail}
        setFrequency={setFrequency}
        frequency={frequency}
        ticketNameOpen={ticketNameOpen}
        setTicketNameOpen={setTicketNameOpen}
        aiLoading={aiLoading}
        newTicketName={newTicketName}
        setNewTicketName={setNewTicketName}
        isInteractionDisabled={isInteractionDisabled}
        tableResetSignal={tableResetSignal}
        setBeneficialOwnership={setBeneficialOwnership}
        setAdverseMedia={setAdverseMedia}
        hasRunOnce={hasRunOnce}
        entityInputMode={entityInputMode}
        setEntityInputMode={setEntityInputMode}
        entityScreenReason={screenReason}
      />
      <Modal
        open={filterMode}
        onClose={() => setFilterMode(false)}
        sx={{ zIndex: 900 }}
      >
        <EntityFilterOptions
          inputs={inputs}
          entityFiltersTotal={entityFiltersTotal.data}
          setFilterMode={setFilterMode}
          lensOpts={lensOpts}
          submit={submit}
          setSubmit={setSubmit}
          countryOpts={countryOpts}
          agencyOpts={agencyOpts}
          listOpts={listOpts}
          entityState={entityState}
          setEntityState={setEntityState}
          appliedFilter={appliedFilter}
          setAppliedFilter={setAppliedFilter}
          handleClear={handleClear}
          countOpts={countOpts}
          agOpts={agOpts}
          filters={filters}
          setFilters={setFilters}
          cur_user={cur_user}
        />
      </Modal>
      <Modal open={openDate} onClose={() => setOpenDate(false)}>
        <DateSelector
          openDate={openDate}
          setOpenDate={setOpenDate}
          state={entityState}
          handleDateChange={handleDateChangeNew}
          setSubmit={setSubmit}
          submit={submit}
        />
      </Modal>
    </div>
  );
};

export default Entities;
